import Label from '../../Utils/FormInputs/Label';
import TextField from '../../Utils/FormInputs/TextField';
import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import IconButton from '@material-ui/core/IconButton';
import Collapse from '@material-ui/core/Collapse';
import Chip from '@material-ui/core/Chip';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import StarIcon from '@material-ui/icons/Star';
import PageTitle from '../../Utils/smsTitle';
import { SubHeader } from '../../../Containers/SubHeader';
import DoctorTypeBadge from '../shared/DoctorTypeBadge';
import { DOCTOR_TYPE } from '../shared/constants';

const useStyles = makeStyles(() => ({
  root: { margin: 22, padding: 20 },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  filterLabel: { fontSize: '0.85rem', fontWeight: 500, color: '#555' },
  filterSelect: { minWidth: 160 },
  searchInput: { minWidth: 220 },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: 12,
    marginTop: 24,
    color: '#333',
  },
  sectionPaper: { padding: 16, marginBottom: 20 },
  tableHead: {
    backgroundColor: '#f5f5f5',
    '& th': { fontWeight: 600, fontSize: '0.8rem', color: '#333' },
  },
  tableCell: { fontSize: '0.85rem' },
  expandedRow: { backgroundColor: '#fafafa' },
  procedureList: { padding: '8px 16px 8px 56px' },
  procedureItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '4px 0',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '0.8rem',
    color: '#555',
  },
  topPerformer: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    borderRadius: 8,
    backgroundColor: '#FFF8E1',
    border: '1px solid #FFD54F',
    marginBottom: 20,
  },
  topPerformerStar: { color: '#FFB300', fontSize: 32 },
  topPerformerName: { fontSize: '1rem', fontWeight: 600, color: '#333' },
  topPerformerDetail: { fontSize: '0.8rem', color: '#777' },
  summaryCard: {
    padding: 16,
    borderRadius: 8,
    textAlign: 'center',
    border: '2px solid',
    minHeight: 80,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  summaryValue: { fontSize: '1.5rem', fontWeight: 700 },
  summaryLabel: { fontSize: '0.8rem', marginTop: 4, color: '#555' },
  ratingChip: {
    fontWeight: 600,
    color: '#fff',
    height: 22,
    fontSize: '0.75rem',
  },
}));

// ── Mock Data ────────────────────────────────────────────────────────────────
const DATE_RANGES = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'custom', label: 'Custom Range' },
];

const SURGEONS = [
  {
    id: 1,
    name: 'Dr. Mugisha James',
    type: DOCTOR_TYPE.INTERNAL,
    specialty: 'General Surgery',
    cases: 32,
    avgDurationMin: 88,
    complications: 1,
    rating: 4.8,
    procedures: [
      { name: 'Laparoscopic Cholecystectomy', count: 12 },
      { name: 'Appendicectomy', count: 8 },
      { name: 'Hernia Repair', count: 7 },
      { name: 'Exploratory Laparotomy', count: 5 },
    ],
  },
  {
    id: 2,
    name: 'Dr. Akullu Esther',
    type: DOCTOR_TYPE.INTERNAL,
    specialty: 'Obstetrics & Gynaecology',
    cases: 28,
    avgDurationMin: 72,
    complications: 0,
    rating: 4.9,
    procedures: [
      { name: 'Caesarean Section', count: 14 },
      { name: 'Myomectomy', count: 8 },
      { name: 'Total Abdominal Hysterectomy', count: 6 },
    ],
  },
  {
    id: 3,
    name: 'Dr. Okello Patrick',
    type: DOCTOR_TYPE.VISITING_CONSULTANT,
    specialty: 'Orthopaedics',
    cases: 18,
    avgDurationMin: 125,
    complications: 2,
    rating: 4.5,
    procedures: [
      { name: 'Open Reduction Internal Fixation', count: 7 },
      { name: 'Total Hip Replacement', count: 5 },
      { name: 'Arthroscopy', count: 4 },
      { name: 'Debridement', count: 2 },
    ],
  },
  {
    id: 4,
    name: 'Dr. Namutebi Florence',
    type: DOCTOR_TYPE.INTERNAL,
    specialty: 'Urology',
    cases: 15,
    avgDurationMin: 95,
    complications: 1,
    rating: 4.6,
    procedures: [
      { name: 'TURP', count: 6 },
      { name: 'Nephrectomy', count: 4 },
      { name: 'Cystoscopy', count: 5 },
    ],
  },
  {
    id: 5,
    name: 'Dr. Byaruhanga Martin',
    type: DOCTOR_TYPE.LOCUM,
    specialty: 'ENT',
    cases: 20,
    avgDurationMin: 55,
    complications: 0,
    rating: 4.7,
    procedures: [
      { name: 'Tonsillectomy', count: 8 },
      { name: 'Septoplasty', count: 6 },
      { name: 'Myringotomy', count: 4 },
      { name: 'FESS', count: 2 },
    ],
  },
];

const ratingColor = (r) => (r >= 4.7 ? '#4CAF50' : r >= 4.3 ? '#FF9800' : '#F44336');

// ── Expandable Row ───────────────────────────────────────────────────────────
const SurgeonRow = ({ surgeon, classes }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell className={classes.tableCell}>{surgeon.name}</TableCell>
        <TableCell>
          <DoctorTypeBadge doctorType={surgeon.type} />
        </TableCell>
        <TableCell className={classes.tableCell}>{surgeon.specialty}</TableCell>
        <TableCell align="center" className={classes.tableCell}>{surgeon.cases}</TableCell>
        <TableCell align="center" className={classes.tableCell}>{surgeon.avgDurationMin} min</TableCell>
        <TableCell align="center" className={classes.tableCell}>{surgeon.complications}</TableCell>
        <TableCell align="center">
          <Chip
            label={surgeon.rating.toFixed(1)}
            size="small"
            className={classes.ratingChip}
            style={{ backgroundColor: ratingColor(surgeon.rating) }}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <div className={classes.procedureList}>
              <Typography style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: 4, color: '#333' }}>
                Procedures Performed
              </Typography>
              {surgeon.procedures.map((p) => (
                <div key={p.name} className={classes.procedureItem}>
                  <span>{p.name}</span>
                  <span style={{ fontWeight: 600 }}>{p.count}</span>
                </div>
              ))}
            </div>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ── Component ────────────────────────────────────────────────────────────────
const SurgeonProductivity = () => {
  const classes = useStyles();
  const [dateRange, setDateRange] = useState('this_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const filtered = SURGEONS.filter(
    (s) =>
      s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.specialty.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const topPerformer = [...SURGEONS].sort((a, b) => b.cases - a.cases)[0];
  const totalCases = SURGEONS.reduce((s, d) => s + d.cases, 0);
  const internalCases = SURGEONS.filter((s) => s.type === DOCTOR_TYPE.INTERNAL).reduce((s, d) => s + d.cases, 0);
  const externalCases = totalCases - internalCases;

  return (
    <div>
      <PageTitle title="Surgeon Productivity" />
      <SubHeader title="Surgery" subTitle="Surgeon Productivity" />

      <Paper className={classes.root} elevation={1}>
        {/* ── Filters ───────────────────────────────────────────────── */}
        <div className={classes.filterRow}>
          <Typography className={classes.filterLabel}>Period:</Typography>
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            variant="outlined"
            size="small"
            className={classes.filterSelect}
          >
            {DATE_RANGES.map((r) => (
              <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
            ))}
          </Select>
          {dateRange === 'custom' && (
            <>
              <TextField
                type="date"
                label="From"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
              />
              <TextField
                type="date"
                label="To"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
              />
            </>
          )}
          <TextField
            placeholder="Search surgeon or specialty..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
            className={classes.searchInput}
          />
        </div>

        {/* ── Top Performer ─────────────────────────────────────────── */}
        <div className={classes.topPerformer}>
          <StarIcon className={classes.topPerformerStar} />
          <div>
            <Typography className={classes.topPerformerName}>
              Top Performer: {topPerformer.name}
            </Typography>
            <Typography className={classes.topPerformerDetail}>
              {topPerformer.cases} cases | {topPerformer.specialty} | Rating {topPerformer.rating.toFixed(1)}
            </Typography>
          </div>
        </div>

        {/* ── Surgeon Table ─────────────────────────────────────────── */}
        <Typography className={classes.sectionTitle}>Surgeon Performance</Typography>
        <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 20 }}>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Surgeon Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Specialty</TableCell>
                <TableCell align="center">Cases</TableCell>
                <TableCell align="center">Avg Duration</TableCell>
                <TableCell align="center">Complications</TableCell>
                <TableCell align="center">Rating</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((surgeon) => (
                <SurgeonRow key={surgeon.id} surgeon={surgeon} classes={classes} />
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" style={{ padding: 24, color: '#999' }}>
                    No surgeons match the search criteria
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── External vs Internal Comparison ───────────────────────── */}
        <Typography className={classes.sectionTitle}>Internal vs External Comparison</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Paper className={classes.summaryCard} elevation={0} style={{ borderColor: '#3F51B5' }}>
              <Typography className={classes.summaryValue} style={{ color: '#3F51B5' }}>
                {totalCases}
              </Typography>
              <Typography className={classes.summaryLabel}>Total Cases (All Surgeons)</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper className={classes.summaryCard} elevation={0} style={{ borderColor: '#4CAF50' }}>
              <Typography className={classes.summaryValue} style={{ color: '#4CAF50' }}>
                {internalCases}
              </Typography>
              <Typography className={classes.summaryLabel}>Internal Surgeons</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper className={classes.summaryCard} elevation={0} style={{ borderColor: '#FF9800' }}>
              <Typography className={classes.summaryValue} style={{ color: '#FF9800' }}>
                {externalCases}
              </Typography>
              <Typography className={classes.summaryLabel}>External / Visiting / Locum</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
};

export default SurgeonProductivity;
